import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { sendSMS } from '../../services/auth';
import './Login.css';

const Login = () => {
  const { login, isLoading } = useAuth();
  const location = useLocation();
  const [formData, setFormData] = useState({
    phone: '',
    code: '',
    inviteCode: ''
  });
  const [error, setError] = useState('');
  const [smsLoading, setSmsLoading] = useState(false);
  const [smsCountdown, setSmsCountdown] = useState(0);
  const [keepLogin, setKeepLogin] = useState(false);
  const [agreeTerms, setAgreeTerms] = useState(false);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search || window.location.search);
    const invite = searchParams.get('invite') || searchParams.get('inviteCode');
    if (invite) {
      setFormData((prev) => (prev.inviteCode === invite ? prev : { ...prev, inviteCode: invite }));
    }
  }, [location.search]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setError('');
  };

  const handleSendSMS = async () => {
    if (!formData.phone) {
      setError('请输入手机号');
      return;
    }

    if (!/^1[3-9]\d{9}$/.test(formData.phone)) {
      setError('请输入有效的手机号');
      return;
    }

    setSmsLoading(true);
    try {
      const result = await sendSMS(formData.phone);
      if (result.ok) {
        // Start countdown
        setSmsCountdown(60);
        const timer = setInterval(() => {
          setSmsCountdown(prev => {
            if (prev <= 1) {
              clearInterval(timer);
              return 0;
            }
            return prev - 1;
          });
        }, 1000);
      } else {
        setError(result.msg || '发送短信失败');
      }
    } catch (error) {
      setError('发送短信失败，请稍后重试');
    } finally {
      setSmsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.phone) {
      setError('请输入手机号');
      return;
    }

    if (!formData.code) {
      setError('请输入验证码');
      return;
    }

    if (!agreeTerms) {
      setError('请先同意用户协议和隐私政策');
      return;
    }

    const credentials = {
      phone: formData.phone,
      password: '',
      code: formData.code,
      inviteCode: formData.inviteCode
    };

    const result = await login(credentials);
    if (!result.success) {
      setError(result.error || '登录失败');
    }
  };

  return (
    <div className="login-page-container">
      {/* Left side - Brand section */}
      <div className="brand-section">
        <div className="brand-background"></div>
        <img alt="logo" fetchPriority="high" width="410" height="116" decoding="async" data-nimg="1" style={{ color: 'transparent', width: 'auto', height: '116px' }} srcSet="https://assets.psyai.net/public/res/release/logo-white.svg 1x, https://assets.psyai.net/public/res/release/logo-white.svg 2x" src="https://assets.psyai.net/public/res/release/logo-white.svg" />
      </div>

      {/* Right side - Login form */}
      <div className="login-section">
        <div className="login-container">
          <div className="login-card">
            <header className="login-header">
              <h2>登录账号</h2>
              <p>欢迎回来，请使用手机号验证码登录</p>
            </header>

            <form onSubmit={handleSubmit} className="login-form">
              {/* Phone Input */}
              <div className="form-group">
                <input
                  type="text"
                  inputMode="numeric"
                  autoComplete="off"
                  id="phone"
                  name="phone"
                  value={formData.phone}
                  onChange={handleInputChange}
                  placeholder="请输入手机号"
                  disabled={isLoading}
                  className="login-field"
                />
              </div>

              {/* SMS Code Input */}
              <div className="form-group">
                <div className="code-input-group">
                  <input
                    type="text"
                    id="code"
                    name="code"
                    value={formData.code}
                    onChange={handleInputChange}
                    placeholder="请输入验证码"
                    disabled={isLoading}
                    className="login-field code-input"
                  />
                  <button
                    type="button"
                    onClick={handleSendSMS}
                    disabled={smsLoading || smsCountdown > 0 || isLoading}
                    className="send-code-btn"
                  >
                    {smsLoading ? '发送中' : smsCountdown > 0 ? `${smsCountdown}s` : '发送'}
                  </button>
                </div>
              </div>

              {/* Keep Login Checkbox */}
              <div className="form-options">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={keepLogin}
                    onChange={(e) => setKeepLogin(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  保持登录
                </label>
              </div>

              {/* Error Message */}
              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              {/* Submit Button */}
              <button
                type="submit"
                className="submit-btn"
                disabled={isLoading}
              >
                {isLoading ? '登录中...' : '登录'}
              </button>

              {/* Terms Agreement */}
              <div className="terms-agreement">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    checked={agreeTerms}
                    onChange={(e) => setAgreeTerms(e.target.checked)}
                    className="checkbox-input"
                  />
                  <span className="checkbox-custom"></span>
                  <span className="terms-text">
                    我已阅读并同意相关协议 <a href="https://app.psyai.com//docs/agreement/user" className="terms-link">隐私政策</a>、<a href="https://app.psyai.com//docs/agreement/user" className="terms-link">用户协议</a>，未注册
                    用户手机登录时会自动创建账号
                  </span>
                </label>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;